import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import CloudflareImage from './CloudflareImage';
// import TailscaleProtection from './TailscaleProtection';
import { getTotalPhotoCount, generateAllPhotos } from '../utils/photoLoader';
import './Photos.css';

const Photos = () => {
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [sortBy, setSortBy] = useState('filename'); // 'filename' or 'index'
  const [searchTerm, setSearchTerm] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  

  // Sort and filter photos
  const processedPhotos = useMemo(() => {
    setIsLoading(true);
    
    let photos = generateAllPhotos();
    
    // Filter by search term
    if (searchTerm) {
      photos = photos.filter(photo => 
        photo.filename?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        photo.title?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Sort photos
    photos.sort((a, b) => {
      if (sortBy === 'filename') {
        return a.filename.localeCompare(b.filename);
      }
      return 0; // Keep original order for index sorting
    });
    
    // Simulate brief loading for large datasets
    setTimeout(() => setIsLoading(false), 100);
    
    return photos;
  }, [searchTerm, sortBy]);

  // Pagination
  const totalPages = Math.ceil(processedPhotos.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentPhotos = processedPhotos.slice(startIndex, startIndex + itemsPerPage);

  const openLightbox = (photo, index) => {
    setSelectedPhoto({ ...photo, globalIndex: startIndex + index });
  };

  const closeLightbox = React.useCallback(() => {
    setSelectedPhoto(null);
  }, []);

  const navigateLightbox = React.useCallback((direction) => {
    if (!selectedPhoto) return;
    
    const currentIndex = selectedPhoto.globalIndex;
    const newIndex = direction === 'next' 
      ? Math.min(currentIndex + 1, processedPhotos.length - 1)
      : Math.max(currentIndex - 1, 0);
    
    if (newIndex !== currentIndex) {
      setSelectedPhoto({ ...processedPhotos[newIndex], globalIndex: newIndex });
    }
  }, [selectedPhoto, processedPhotos]);

  const handleKeyPress = React.useCallback((e) => {
    if (!selectedPhoto) return;
    
    if (e.key === 'Escape') {
      closeLightbox();
    } else if (e.key === 'ArrowLeft') {
      navigateLightbox('prev');
    } else if (e.key === 'ArrowRight') {
      navigateLightbox('next');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPhoto, processedPhotos, closeLightbox, navigateLightbox]);

  React.useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [handleKeyPress]);

  const formatFileSize = (filename) => {
    // Mock file size for demonstration
    return `${Math.floor(Math.random() * 5 + 1)}.${Math.floor(Math.random() * 9)}MB`;
  };

  const getFileIcon = () => {
    return '🖼️';
  };
  

  return (
    <div className="photos-page">
      {/* Header */}
      <div className="photos-header">
        <div className="photos-breadcrumb">
          <Link to="/df" className="breadcrumb-link">DF Wedding</Link>
          <span className="breadcrumb-separator">›</span>
          <span className="breadcrumb-current">Photos</span>
        </div>
        
        <h1 className="photos-title">Wedding Photo Gallery</h1>
        <p className="photos-subtitle">
          {getTotalPhotoCount()} beautiful memories from Fuad & Donika's special day
        </p>
      </div>

      {/* Toolbar */}
      <div className="photos-toolbar">
        <div className="toolbar-left">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search photos..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="search-input"
            />
            <span className="search-icon">🔍</span>
          </div>
          
          <select 
            value={sortBy} 
            onChange={(e) => setSortBy(e.target.value)}
            className="sort-select"
          >
            <option value="filename">Sort by Filename</option>
            <option value="index">Sort by Original Order</option>
          </select>
        </div>
        
        <div className="toolbar-right">
          <div className="view-controls">
            <button
              onClick={() => setViewMode('grid')}
              className={`view-button ${viewMode === 'grid' ? 'active' : ''}`}
              title="Grid View"
            >
              ⊞
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`view-button ${viewMode === 'list' ? 'active' : ''}`}
              title="List View"
            >
              ☰
            </button>
          </div>
          
          <select 
            value={itemsPerPage} 
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="items-per-page-select"
          >
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
            <option value={200}>200 per page</option>
            <option value={500}>500 per page</option>
          </select>
        </div>
      </div>

      {/* Results Info */}
      <div className="photos-info">
        Showing {startIndex + 1}–{Math.min(startIndex + itemsPerPage, processedPhotos.length)} of {processedPhotos.length} photos
        {searchTerm && (
          <span className="search-results"> (filtered from {getTotalPhotoCount()} total)</span>
        )}
      </div>

      {/* Photo Gallery */}
      <div className={`photos-container ${viewMode}`}>
        {isLoading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading {processedPhotos.length.toLocaleString()} photos...</p>
          </div>
        ) : currentPhotos.length === 0 ? (
          <div className="no-results">
            <div className="no-results-icon">📷</div>
            <h3>No photos found</h3>
            <p>Try adjusting your search terms</p>
          </div>
        ) : (
          currentPhotos.map((photo, index) => (
            <div
              key={photo.id}
              className="photo-item"
              onClick={() => openLightbox(photo, index)}
            >
              {viewMode === 'grid' ? (
                <div className="photo-grid-item">
                  <CloudflareImage
                    imageId={photo.id}
                    variant="public"
                    alt={photo.filename}
                    className="photo-thumbnail"
                  />
                  <div className="photo-overlay">
                    <span className="photo-filename">{photo.filename}</span>
                  </div>
                </div>
              ) : (
                <div className="photo-list-item">
                  <div className="photo-icon">{getFileIcon()}</div>
                  <div className="photo-details">
                    <div className="photo-name">{photo.filename}</div>
                    <div className="photo-meta">
                      <span className="photo-size">{formatFileSize(photo.filename)}</span>
                      <span className="photo-type">JPEG Image</span>
                    </div>
                  </div>
                  <CloudflareImage
                    imageId={photo.id}
                    variant="public"
                    alt={photo.filename}
                    className="photo-list-thumbnail"
                  />
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="photos-pagination">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="pagination-button"
          >
            ← Previous
          </button>
          
          <div className="pagination-info">
            Page {currentPage} of {totalPages}
          </div>
          
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="pagination-button"
          >
            Next →
          </button>
        </div>
      )}

      {/* Lightbox */}
      {selectedPhoto && (
        <div className="photos-lightbox" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox}>×</button>
            
            {processedPhotos.length > 1 && (
              <>
                <button 
                  className="lightbox-nav lightbox-prev" 
                  onClick={() => navigateLightbox('prev')}
                  disabled={selectedPhoto.globalIndex === 0}
                >
                  ‹
                </button>
                <button 
                  className="lightbox-nav lightbox-next" 
                  onClick={() => navigateLightbox('next')}
                  disabled={selectedPhoto.globalIndex === processedPhotos.length - 1}
                >
                  ›
                </button>
              </>
            )}
            
            <CloudflareImage
              imageId={selectedPhoto.id}
              variant="public"
              alt={selectedPhoto.filename}
              className="lightbox-image"
            />
            
            <div className="lightbox-info">
              <h3>{selectedPhoto.filename}</h3>
              <p>Photo {selectedPhoto.globalIndex + 1} of {processedPhotos.length}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Photos;